import { createAsyncThunk } from '@reduxjs/toolkit'
import apiClient from '../../../services/axois'
import { ROUTES_PATH } from '../../../utility/constants'
import { toast } from 'react-toastify'
import AWS from 'aws-sdk'
import { setLoading } from '../../slice/course/course.slice'

const S3_BUCKET = process.env.REACT_APP_S3_BUCKET
const REGION = process.env.REACT_APP_REGION

AWS.config.update({
  // eslint-disable-next-line no-undef
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY,
  // eslint-disable-next-line no-undef
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_KEY,
})

const s3 = new AWS.S3({
  params: { Bucket: S3_BUCKET },
  region: REGION,
})

export const getCourseMaterials = createAsyncThunk('course/getMaterials', async () => {
  try {
    const response = await apiClient.get('/material')
    return response.data.result
  } catch (error) {
    console.error('builder function error:', error)
  }
})

export const getCourse = createAsyncThunk('course/getCourse', async (courseStatus) => {
  const url = courseStatus ? `${`/courses?courseStatus=${courseStatus}`}` : '/courses'
  try {
    const response = await apiClient.get(url)
    return response?.data?.result?.courses
  } catch (error) {
    console.error('builder function error:', error)
    throw error
  }
})

export const createCourseInformation = createAsyncThunk(
  'course/create-course-information',
  async ({ courseName, aliasName, checked, materialId, navigate, toast }) => {
    try {
      const response = await apiClient.post('/course', {
        courseName,
        courseStatus: 'draft',
        courseAliasName: aliasName,
        courseExamFormat: checked,
        materialId,
      })

      if (response.status) {
        toast.success(response.data.message, {
          className: 'custom-toast-success',
          progressStyle: { background: '#ffffff' },
        })
      }

      navigate(ROUTES_PATH.ADD_COURSE_CONTENT)
      return response.data.result.courseId
    } catch (error) {
      console.error(error)
      toast.error(error.response.data.message, {
        className: 'custom-toast-error',
        progressStyle: { background: '#C53C43' },
      })
    }
  },
)

export const editCourseInformation = createAsyncThunk('course/edit-course-information', async (obj) => {
  const { courseId, courseName, aliasName: courseAliasName, checked: courseExamFormat, materialId } = obj

  try {
    const response = await apiClient.put(`/course/${courseId}`, {
      courseName,
      courseStatus: 'draft',
      courseAliasName,
      courseExamFormat,
      materialId,
    })

    if (response.status) {
      toast.success(response.data.message, {
        className: 'custom-toast-success',
        progressStyle: { background: '#428D56' },
      })
    }

    if (obj.hasId) {
      obj.navigate(ROUTES_PATH.COURSE)
    } else {
      obj.navigate(ROUTES_PATH.ADD_COURSE_CONTENT)
    }
  } catch (error) {
    console.error(error)
  }
})

export const createCourseContentModules = createAsyncThunk('course/create-course-content', async (obj) => {
  obj.dispatch(setLoading(true))
  try {
    const { courseId } = obj
    const moduleData = obj.modules.map((module) => {
      return {
        moduleName: module.moduleName,
        topics: module.topic.map((topic) => {
          return topic.topicName
        }),
      }
    })

    const response = await apiClient.post('/course/content', {
      courseId,
      moduleData,
    })

    if (response.status) {
      toast.success(response.data.message, {
        className: 'custom-toast-success',
        progressStyle: { background: '#ffffff' },
      })
    }

    const getVideoDuration = (file) => {
      return new Promise((resolve) => {
        const videoElement = document.createElement('video')
        videoElement.preload = 'metadata'

        videoElement.onloadedmetadata = () => {
          window.URL.revokeObjectURL(videoElement.src)
          resolve(videoElement.duration)
        }

        videoElement.src = URL.createObjectURL(file)
      })
    }

    const uploadFileToS3 = async (file) => {
      const timestamp = Date.now()
      const originalUrl = `${timestamp}_${file.name.toLowerCase().replace(/ /g, '_')}`

      const params = {
        Bucket: S3_BUCKET,
        Key: `videos/${originalUrl}`,
        Body: file,
        Expires: 1200,
      }

      const upload = s3.upload(params).promise()
      const duration = await getVideoDuration(file)
      return upload
        .then((data) => {
          return { location: data.Location, originalUrl, duration }
        })
        .catch((err) => {
          console.error(err)
          throw new Error('Error uploading to S3')
        })
    }

    const uploadFiles = async (topic, findTopicFile) => {
      try {
        const formData = new FormData()
        const videoUrls = []

        topic.pdfFiles.forEach((file) => {
          if (file instanceof File) {
            formData.append('files', file)
          }
        })

        topic.mcqFile.forEach((file) => {
          if (file instanceof File) {
            formData.append('files', file)
          }
        })

        const videoUploadPromises = topic.videoFiles
          .filter((file) => file instanceof File)
          .map((file) => uploadFileToS3(file))

        const uploadedVideos = await Promise.all(videoUploadPromises)
        uploadedVideos.forEach(({ location, originalUrl, duration }) => {
          videoUrls.push({ url: location, originalUrl, duration })
        })

        if (formData.has('files')) {
          formData.append('courseModuleTopicId', findTopicFile.course_module_topic_id)
          formData.append('courseModuleId', findTopicFile.course_module_id)
          formData.append('courseId', findTopicFile.course_id)

          const docResponse = await apiClient.post('/course/module/topic/upload-doc', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })

          if (docResponse.status) {
            toast.success(docResponse.data.message, {
              className: 'custom-toast-success',
              progressStyle: { background: '#ffffff' },
            })
          }
        }

        if (videoUrls.length > 0) {
          const videoResponse = await apiClient.post('/course/module/topic/upload-video', {
            course_video_urls: videoUrls.map((video) => ({
              url: video.url,
              originalUrl: video.originalUrl,
              duration: video.duration,
            })),
            courseId: findTopicFile.course_id,
            moduleId: findTopicFile.course_module_id,
            topicId: findTopicFile.course_module_topic_id,
          })

          if ([200, 201].includes(videoResponse.status)) {
            toast.success('Videos uploaded successfully.', {
              className: 'custom-toast-success',
              progressStyle: { background: '#ffffff' },
            })
            obj.dispatch(setLoading(false))
          }
        }
      } catch (error) {
        console.error('Error uploading files:', error)
        obj.dispatch(setLoading(false))
        toast.error(error.response.data.message, {
          className: 'custom-toast-error',
          progressStyle: { background: '#C53C43' },
        })
      }
    }

    for (const data of obj.modules) {
      for (const topic of data.topic) {
        if (topic.pdfFiles?.length > 0 || topic.videoFiles?.length > 0 || topic.mcqFile?.length > 0) {
          const findTopicFile = response.data.result.find((i) => i.course_module_topic_name === topic.topicName)

          if (findTopicFile) {
            await uploadFiles(topic, findTopicFile)
          }
        }
      }
    }

    if (obj.editNavigate) {
      obj.navigate(ROUTES_PATH.ADD_COURSE_PRICING)
    }
  } catch (error) {
    toast.error(error.response.data.message, {
      className: 'custom-toast-error',
      progressStyle: { background: '#C53C43' },
    })
  }
})

export const editCourseContent = createAsyncThunk(
  'course/edit-course-content',
  async ({ modules, courseId, navigate, to, dispatch }) => {
    dispatch(setLoading(true))
    try {
      const moduleData = modules.map((module) => ({
        moduleName: module?.moduleName,
        moduleId: module?.moduleId || '',
        topics: module.topic.map((topic) => ({
          topicId: topic.topicId,
          topicName: topic.topicName,
        })),
      }))

      const response = await apiClient.put(`/course/content/module`, {
        courseId,
        moduleData,
      })

      const getVideoDuration = (file) => {
        return new Promise((resolve) => {
          const videoElement = document.createElement('video')
          videoElement.preload = 'metadata'

          videoElement.onloadedmetadata = () => {
            window.URL.revokeObjectURL(videoElement.src)
            resolve(videoElement.duration)
          }

          videoElement.src = URL.createObjectURL(file)
        })
      }

      const uploadFileToS3 = async (file) => {
        const timestamp = Date.now()
        const originalUrl = `${timestamp}_${file.name.toLowerCase().replace(/ /g, '_')}`

        const params = {
          Bucket: S3_BUCKET,
          Key: `videos/${originalUrl}`,
          Body: file,
          Expires: 1200,
        };

        // Create a unique toast for each file
        const toastId = toast.loading(`Uploading ${file.name}... 0%`);

        return new Promise((resolve, reject) => {
          const upload = s3.upload(params);
          const duration = getVideoDuration(file);

          upload.on('httpUploadProgress', (progress) => {
            const percentCompleted = Math.round((progress.loaded * 100) / progress.total);

            // Update the same toast dynamically
            toast.update(toastId, {
              className: 'custom-toast-success',
              progressStyle: { background: '#ffffff' },
              render: `Uploading ${file.name}... ${percentCompleted}%`,
              autoClose: false,
              isLoading: true,
            });
          });

          upload.promise()
            .then((data) => {
              toast.update(toastId, {
                type: 'success',
                isLoading: false,
                autoClose: 3000, // Close after 3 seconds
              });
              resolve({ location: data.Location, originalUrl, duration });
            })
            .catch((err) => {
              toast.update(toastId, {
                render: `Error uploading ${file.name} ❌`,
                type: 'error',
                isLoading: false,
                autoClose: 3000,
              });
              reject(err);
            });
        })
      }

      const uploadFiles = async (topic, findTopicFile) => {
        try {
          const formData = new FormData()
          const videoUrls = []

          topic.pdfFiles.forEach((file) => {
            if (file instanceof File) {
              formData.append('files', file)
            }
          })

          topic.mcqFile.forEach((file) => {
            if (file instanceof File) {
              formData.append('files', file)
            }
          })

          const videoUploadPromises = topic.videoFiles
            .filter((file) => file instanceof File)
            .map((file) => uploadFileToS3(file))

          const uploadedVideos = await Promise.all(videoUploadPromises)
          uploadedVideos.forEach(({ location, originalUrl, duration }) => {
            videoUrls.push({ url: location, originalUrl, duration })
          })

          if (formData.has('files')) {
            formData.append('courseModuleTopicId', findTopicFile.course_module_topic_id)
            formData.append('courseModuleId', findTopicFile.course_module_id)
            formData.append('courseId', findTopicFile.course_id)

            const docResponse = await apiClient.post('/course/module/topic/upload-doc', formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            })

            if (docResponse.status) {
              toast.success(docResponse.data.message, {
                className: 'custom-toast-success',
                progressStyle: { background: '#ffffff' },
              })
            }
          }

          if (videoUrls.length > 0) {
            const videoResponse = await apiClient.post('/course/module/topic/upload-video', {
              course_video_urls: videoUrls.map((video) => ({
                url: video.url,
                originalUrl: video.originalUrl,
                duration: video.duration,
              })),
              courseId: findTopicFile.course_id,
              moduleId: findTopicFile.course_module_id,
              topicId: findTopicFile.course_module_topic_id,
            })

            if (videoResponse.status === 200) {
              toast.success('Videos uploaded successfully.', {
                className: 'custom-toast-success',
                progressStyle: { background: '#ffffff' },
              })
              dispatch(setLoading(false))
            }
          }
        } catch (error) {
          console.error('Error uploading files:', error)
          dispatch(setLoading(false))
          toast.error(error.response.data.message, {
            className: 'custom-toast-error',
            progressStyle: { background: '#C53C43' },
          })
        }
      }

      for (const data of modules) {
        for (const topic of data.topic) {
          if (topic.pdfFiles?.length > 0 || topic.videoFiles?.length > 0 || topic.mcqFile?.length > 0) {
            const findTopicFile = response.data.result.find((i) => i.course_module_topic_name === topic.topicName)

            if (findTopicFile) {
              await uploadFiles(topic, findTopicFile)
            }
          }
        }
      }

      if (to === 'courseList') {
        navigate(ROUTES_PATH.COURSE)
      }

      if (to === 'coursePricing') {
        navigate(ROUTES_PATH.ADD_COURSE_PRICING)
      }

      dispatch(setLoading(false))
    } catch (error) {
      dispatch(setLoading(false))
    }
  },
)

export const getBranchList = createAsyncThunk('course/get-branch-list', async (status) => {
  try {
    // const response = await apiClient.get('/branches')
    let response
    if (status) {
      response = await apiClient.get(`/branches?branchStatus=${status}`)
    } else {
      response = await apiClient.get(`/branches`)
    }
    const branches = response.data.result.branches.map((branch) => {
      return {
        branchId: branch.branchId,
        branchName: branch.branchName,
        branchCity: branch.cityName,
      }
    })
    return branches
  } catch (error) {
    console.error('builder function error:', error)
    throw error
  }
})

export const getBatchList = createAsyncThunk(
  'course/get-batch-list',
  async ({ value: branchId, courseId, branchFees }) => {
    const url = branchFees
      ? `/branch/batch/${branchId}/${courseId}?branchFees=${branchFees}`
      : `/branch/batch/${branchId}/${courseId}`
    try {
      const response = await apiClient.get(url)
      const batches = response.data.result.Batch.map((batch) => {
        return {
          batchId: batch.batchId,
          batchNo: batch.batchNo,
        }
      })
      return batches
    } catch (error) {
      console.error('builder function error:', error)
      throw error
    }
  },
)

export const createCourseFees = createAsyncThunk(
  'course/create-course-fees',
  async ({ branchName: branchId, courseId, batchName: batchId, oneShotPay, installmentPay, branchNames, dispatch }) => {
    if (!branchNames.includes(branchId)) {
      const response = await apiClient.post(`/course/branch/assign-pricing/${courseId}`, {
        branchId,
      })
      if (response.status) {
        toast.success(response.data.message, {
          className: 'custom-toast-success',
          progressStyle: { background: '#ffffff' },
        })
      }
    }

    try {
      const createResponse = await apiClient.post(`/course/branch/fees`, {
        courseId,
        branchId,
        batchId,
        installmentFees: installmentPay,
        onetimeFees: oneShotPay,
      })

      if (createResponse.status) {
        toast.success(createResponse.data.message, {
          className: 'custom-toast-success',
          progressStyle: { background: '#ffffff' },
        })
      }

      const response = await apiClient.get(`/course/branch/fees/${courseId}/${branchId}`)

      const courseFees = response.data.result.map((result) => {
        return {
          branchName: result.branch.branchName,
          batchNo: result.batch.batchNo,
          batchDate: result.batch.reflectedDate,
          oneShotPay: result.onetimeFees,
          installmentPay: result.installmentFees,
        }
      })
      dispatch(getCourseByCourseId(courseId))
      return courseFees
    } catch (error) {
      toast.error(error.response.data.message, {
        className: 'custom-toast-error',
        progressStyle: { background: '#C53C43' },
      })
      throw error
    }
  },
)

export const deleteModulesAndTopics = createAsyncThunk('course/delete-module-topic', async ({ id, type }) => {
  try {
    if (type === 'module' && id) {
      const response = await apiClient.delete(`/course/module/topic`, {
        data: { moduleId: id },
      })
      if (response.status) {
        toast.success(response.data.message, {
          className: 'custom-toast-success',
          progressStyle: { background: '#ffffff' },
        })
      }
    }

    if (type === 'topic' && id) {
      const response = await apiClient.delete(`/course/module/topic`, {
        data: { topicId: id },
      })
      if (response.status) {
        toast.success(response.data.message, {
          className: 'custom-toast-success',
          progressStyle: { background: '#ffffff' },
        })
      }
    }
  } catch (error) {
    toast.error(error.response.data.message, {
      className: 'custom-toast-error',
      progressStyle: { background: '#C53C43' },
    })
    throw error
  }
})

export const assignBranchToCourse = createAsyncThunk(
  'course/assign-branch',
  async ({ courseId, selectedButtons, dispatch }) => {
    try {
      const response = await apiClient.post(`/course/branch/assign/${courseId}`, {
        branchIds: selectedButtons,
      })
      if (response.status) {
        toast.success(response.data.message, {
          className: 'custom-toast-success',
          progressStyle: { background: '#ffffff' },
        })
      }
      dispatch(getCourse())
    } catch (error) {
      toast.error(error.response.data.message, {
        className: 'custom-toast-error',
        progressStyle: { background: '#C53C43' },
      })
      throw error
    }
  },
)

export const archiveCourse = createAsyncThunk('course/archive-branch', async ({ courseId, toast, dispatch }) => {
  try {
    const response = await apiClient.post(`/course/${courseId}/archive`, {
      courseStatus: 'archive',
    })
    dispatch(getCourse())
    toast.success(response.data.message, {
      className: 'custom-toast-success',
      progressStyle: { background: '#428D56' },
    })
  } catch (error) {
    // console.error('builder function error:', error)
    toast.error(error.response.data.message, {
      className: 'custom-toast-error',
      progressStyle: { background: '#C53C43' },
    })
    throw error
  }
})

export const deleteUploadedDocument = createAsyncThunk(
  'course/delete-document',
  async ({ fileName, s3Url, currentTopicId }) => {
    try {
      if (s3Url) {
        const response = await apiClient.delete(`/course/module/topic/document`, {
          data: { topicId: currentTopicId, fileName },
        })

        const params = { Bucket: S3_BUCKET, Key: `videos/${fileName}` }

        s3.deleteObject(params, (err, data) => {
          if (err) {
            console.log(`Failed to delete file: ${err}`)
          } else {
            console.log(`File deleted successfully.`)
          }
        })

        if (response.status) {
          toast.success(response.data.message, {
            className: 'custom-toast-success',
            progressStyle: { background: '#ffffff' },
          })
        }
      }
    } catch (error) {
      toast.error(error.response.data.message, {
        className: 'custom-toast-error',
        progressStyle: { background: '#C53C43' },
      })
      throw error
    }
  },
)

export const getCourseByCourseId = createAsyncThunk('course/course-by-id', async (courseId) => {
  try {
    const response = await apiClient.get(`/course/${courseId}`)
    return response.data.result
  } catch (error) {
    console.error('builder function error:', error)
    throw error
  }
})

export const getCoursePricing = createAsyncThunk('course/price-by-id', async ({ courseId, branchId }) => {
  try {
    const response = await apiClient.get(`/course/branch/fees/${courseId}/${branchId}`)
    const courseFees = response.data.result.map((result) => {
      return {
        branchName: result.branch.branchName,
        batchDate: result.batch.reflectedDate,
        oneShotPay: result.onetimeFees,
        installmentPay: result.installmentFees,
        batchNo: result.batch.batchNo,
      }
    })

    return courseFees
  } catch (error) {
    console.error('builder function error:', error)
    throw error
  }
})

export const getCourseBranchFees = createAsyncThunk(
  'course/branch/fees',
  async ({ courseId, branchId, batchId, studentId }) => {
    try {
      let url = studentId
        ? `/course/branch/fees/${courseId}/${batchId}/${branchId}?studentId=${studentId}`
        : `/course/branch/fees/${courseId}/${batchId}/${branchId}`
      const response = await apiClient.get(url)
      return response?.data?.result
    } catch (error) {
      console.error('builder function error:', error)
      throw error
    }
  },
)

export const getCourseListByBranchId = createAsyncThunk('course/getCourseListByBranchId', async (branchId) => {
  try {
    const response = await apiClient.get(`/branch/course/${branchId}`)
    return response?.data?.result
  } catch (error) {
    console.error('builder function error:', error)
    throw error
  }
})

export const getCourseListWithDraftByBranchId = createAsyncThunk(
  'course/getCourseListWithDraftByBranchId',
  async (branchId) => {
    try {
      const response = await apiClient.get(`/branch/course/withdraft/${branchId}`)
      return response?.data?.result
    } catch (error) {
      console.error('builder function error:', error)
      throw error
    }
  },
)
